/**
 * Prashasta Event Journey — Contextual Help Configuration
 * All content is curated and deterministic. No AI in this iteration.
 * Keys match exactly the currentStepId values from BuildMyEvent.jsx.
 */

// ── Analytics event names ─────────────────────────────────────────────────────
export const HELP_EVENTS = {
  OPENED:          'ASK_OPENED',
  TOPIC_SELECTED:  'ASK_TOPIC_SELECTED',
  FREE_TEXT:       'ASK_FREE_TEXT',
  UNANSWERED:      'ASK_UNANSWERED',
  PLANNER_HANDOFF: 'ASK_PLANNER_HANDOFF',
  CLOSED:          'ASK_CLOSED',
};

// ── Help topics keyed by step ID ─────────────────────────────────────────────
export const HELP_TOPICS = {

  DATE_CONFIRM: {
    stepLabel: 'Your Date',
    questions: [
      {
        id: 'date_not_final',
        question: 'What if my date is not final yet?',
        answer: "That's completely fine. Many customers begin planning before their date is confirmed. You can update your event date at any time — we just ask so we can give you an accurate picture of planning availability.",
      },
      {
        id: 'change_date_later',
        question: 'Can I change my date later?',
        answer: "Yes. The date you provide here is a planning estimate. Your actual event date is confirmed as part of the formal planning process with your Prashasta planner.",
      },
      {
        id: 'date_reserve',
        question: 'Does checking a date reserve it?',
        answer: "No. Checking availability only shows you our current planning capacity — it does not hold a slot or create any reservation. Your planner will confirm availability during your first conversation.",
      },
    ],
  },

  EVENT_TYPE: {
    stepLabel: 'Your Event',
    questions: [
      {
        id: 'multiple_events',
        question: "What if I'm planning more than one event?",
        answer: "Select your primary celebration here. Once you connect with your planner, they can help plan the full set — whether it's a multi-day wedding programme or a conference with a gala dinner.",
      },
      {
        id: 'unsure_type',
        question: "I'm not sure which category fits my event.",
        answer: "Choose 'Something Else' — your planner will ask the right questions to understand what you're planning. No event is too unique for Prashasta.",
      },
      {
        id: 'event_size',
        question: 'Does the event type affect the planning process?',
        answer: "Yes — each event type has a different planning rhythm. A wedding involves multiple functions and a longer timeline, while a corporate conference or product launch is driven by a brief and stakeholder schedule. Your experience is tailored from the first step.",
      },
    ],
  },

  WEDDING_FUNCTIONS: {
    stepLabel: 'Wedding Celebrations',
    questions: [
      {
        id: 'all_functions_now',
        question: 'Do I need to plan all functions right now?',
        answer: "No. Select the celebrations you're currently thinking about. You can refine the list with your planner — functions can be added or removed as your planning evolves.",
      },
      {
        id: 'same_venue_functions',
        question: 'Do all functions need to be at the same venue?',
        answer: "Not necessarily. Intimate functions like Mehendi or Haldi are often held at home, while the main Wedding and Reception are at a larger venue. Your planner will help you think through the logistics across all functions.",
      },
      {
        id: 'functions_separate_budgets',
        question: 'Does each function need a separate budget?',
        answer: "Not at this stage. Your overall investment range gives your planner a starting point. A detailed function-by-function budget is developed during the planning process once scope is clearer.",
      },
    ],
  },

  CONFERENCE_TYPE: {
    stepLabel: 'Your Conference',
    questions: [
      {
        id: 'conference_vs_summit',
        question: "What's the difference between a Conference and a Partner Summit?",
        answer: "A Conference is typically larger and open to a broader audience — presentations, panels, and knowledge sharing. A Partner Summit is a more intimate, curated gathering for close business relationships. The planning approach, venue type, and experience design differ significantly.",
      },
      {
        id: 'conference_entertainment',
        question: 'Can Prashasta add an awards dinner or entertainment?',
        answer: "Yes. Many corporate clients combine a conference or summit with an evening awards ceremony, gala, or entertainment experience. This can be included as part of your planning scope.",
      },
    ],
  },

  SCOPE: {
    stepLabel: 'Planning Scope',
    questions: [
      {
        id: 'end_to_end_include',
        question: 'What does end-to-end planning include?',
        answer: "End-to-end means Prashasta manages every key aspect — from venue sourcing and vendor coordination to guest experience and event day oversight. You make the decisions; we handle all the coordination, timelines, and logistics.",
      },
      {
        id: 'choose_specific_services',
        question: 'Can I choose only a few services?',
        answer: "Yes. Select 'I need help with a few things' and you'll be able to choose exactly which areas you'd like support with — whether it's just venue and decor or only photography.",
      },
      {
        id: 'add_services_later',
        question: 'Can I add more services later?',
        answer: "Absolutely. Your initial selection guides the first conversation. During planning discussions, you can always expand or adjust the scope with your planner.",
      },
    ],
  },

  SCOPE_DEFAULTS: {
    stepLabel: 'Your Services',
    questions: [
      {
        id: 'scope_defaults_change',
        question: 'Can I change the pre-selected services?',
        answer: "Yes. The services shown are standard essentials for most events. You can customise this selection — either add or remove — by clicking 'Customise'.",
      },
      {
        id: 'scope_defaults_what_covers',
        question: 'What does each service category include?',
        answer: "Each category covers a broad set of responsibilities. For example, Venue includes sourcing, negotiation, site visits and booking. Decor covers creative direction, styling, floral and lighting. Your planner will walk through each category in detail.",
      },
    ],
  },

  SERVICE_SELECTION: {
    stepLabel: 'Your Services',
    questions: [
      {
        id: 'venue_vs_decor',
        question: "What's the difference between Venue and Decor & Styling?",
        answer: "Venue covers finding, negotiating, booking and managing the event space itself. Decor & Styling covers the creative direction, theme, floral arrangements, lighting and visual presentation within that space.",
      },
      {
        id: 'av_production',
        question: 'What does AV & Production include?',
        answer: "AV & Production covers stage setup, sound systems, lighting rigs, LED screens, live streaming coordination and the full technical infrastructure that creates the atmosphere and experience of your event.",
      },
      {
        id: 'guest_hospitality',
        question: 'What is Guest Hospitality?',
        answer: "Guest Hospitality covers the experience of your guests — reception management, welcome kits, concierge, on-site coordination and ensuring every guest feels looked after from arrival to departure.",
      },
    ],
  },

  LOCATION: {
    stepLabel: 'Your Location',
    questions: [
      {
        id: 'other_city',
        question: 'What if I want the event in a different city?',
        answer: "Select 'Other' and type your preferred city. Prashasta can plan events across India. Your planner will discuss the logistics, local vendor network and travel coordination for your chosen location.",
      },
      {
        id: 'multi_city',
        question: 'What if the event spans multiple cities?',
        answer: "Let us know your primary city here. Multi-city planning — such as a destination wedding — can be discussed with your planner. It's not uncommon, and we have experience coordinating across locations.",
      },
    ],
  },

  GUEST_COUNT: {
    stepLabel: 'Your Guests',
    questions: [
      {
        id: 'dont_know_count',
        question: "What if I don't know the final count?",
        answer: "That's perfectly normal at the early planning stage. A rough estimate helps your planner think about venue capacity and logistics. Final guest counts are typically confirmed much closer to the event date.",
      },
      {
        id: 'count_can_change',
        question: 'Can the guest count change later?',
        answer: "Yes. What you provide here is a working estimate to guide planning direction. Final numbers for catering, seating and logistics are confirmed during the detailed planning phase.",
      },
      {
        id: 'why_estimate',
        question: 'Why do you need an estimate now?',
        answer: "Guest count directly influences venue capacity, catering quantities, seating arrangements and overall event logistics. Even a rough range helps your planner recommend the right options from the start.",
      },
    ],
  },

  VENUE_STATUS: {
    stepLabel: 'Your Venue',
    questions: [
      {
        id: 'venue_find',
        question: 'Can Prashasta help me find a venue?',
        answer: "Yes. If you select 'Help me find one', your planner will recommend venues based on your city, guest count, event type and style preferences. We have strong relationships with venues across major Indian cities.",
      },
      {
        id: 'shortlisting_means',
        question: "What does 'shortlisting' mean?",
        answer: "It means you've visited or researched a few options but haven't committed yet. Prashasta can help you compare, negotiate and finalise the right venue from your shortlist — including site visits and contract review.",
      },
      {
        id: 'planning_before_venue',
        question: 'Can planning start before the venue is confirmed?',
        answer: "Yes — and in most cases it's recommended. Decor concepts, vendor selection, and guest management planning can all begin before the venue is locked. Your planner will advise what runs in parallel.",
      },
      {
        id: 'confirm_before_planning',
        question: 'Should I confirm the venue before planning anything else?',
        answer: "Not necessarily. Starting early gives you more time and better choices. Your planner can help evaluate venues as part of the planning process itself — rather than waiting until a venue is confirmed.",
      },
    ],
  },

  STYLE_PREF: {
    stepLabel: 'Your Style',
    questions: [
      {
        id: 'elegant_vs_luxury',
        question: 'What is the difference between elegant and luxury?',
        answer: "Elegant events focus on refined aesthetics, clean lines and tasteful details — beautiful without being extravagant. Luxury events go further in premium materials, exclusive vendors and highly personalised experiences. Both can be stunning; the difference is often in scale and investment level.",
      },
      {
        id: 'combine_styles',
        question: 'Can you combine traditional and contemporary?',
        answer: "Absolutely. Blended aesthetics are very popular in modern Indian events — for example, a traditional ceremony with a contemporary reception setting. Your Prashasta designer will help bring your combined vision together coherently.",
      },
      {
        id: 'dont_know_style',
        question: "Do I need to know my style right now?",
        answer: "Not at all. 'Not Sure Yet' is a completely valid choice. Many customers discover their preferred direction through a moodboard session and early concept discussions with their planner. This is just the beginning of the conversation.",
      },
    ],
  },

  BUDGET: {
    stepLabel: 'Your Investment',
    questions: [
      {
        id: 'why_budget',
        question: 'Why do you ask for a budget range?',
        answer: "Knowing your investment range helps your planner recommend appropriate venues, vendors and concepts. It's not a commitment — it simply helps us give you realistic and relevant options rather than wasting your time on suggestions that don't align.",
      },
      {
        id: 'not_final_price',
        question: 'Is this my final event price?',
        answer: "No. This is an initial guide to help your planner understand your direction. Final pricing is determined after detailed scope discussions, vendor selection and the formal planning brief.",
      },
      {
        id: 'discuss_with_planner',
        question: 'Can I discuss budget directly with a planner?',
        answer: "Yes. Select 'I'd rather discuss this' and your planner will have a private, confidential conversation about investment, phased planning options, and how to create the best event within your comfort level.",
      },
    ],
  },

  CONTACT: {
    stepLabel: 'Saving Your Plan',
    questions: [
      {
        id: 'why_contact',
        question: 'Why do you need my contact details?',
        answer: "Your contact information allows Prashasta to link your event plan to a dedicated planner. Without it, your plan exists only in this browser. Providing your name and mobile means we can resume your journey from any device and connect you with the right planner.",
      },
      {
        id: 'email_optional',
        question: 'Is email required?',
        answer: "Email is optional. Your mobile number is the primary way your planner will reach you. An email address allows us to send you a summary copy of your event plan if you'd like one.",
      },
      {
        id: 'data_shared',
        question: 'Who will see my information?',
        answer: "Your details are shared only with your dedicated Prashasta planner. We do not share your information with third parties. Your plan is used solely to begin your planning conversation.",
      },
    ],
  },

  RESULT: {
    stepLabel: 'Your Event Plan',
    questions: [
      {
        id: 'planner_see_info',
        question: 'Will my planner see everything I entered?',
        answer: "Yes. When you connect via WhatsApp, your planner will already have your complete event plan — event type, date, city, guest count, services and style preferences. You won't need to repeat yourself.",
      },
      {
        id: 'repeat_everything',
        question: 'Do I need to explain everything again on WhatsApp?',
        answer: "No. The purpose of Build My Event is to give your planner a head start. Your first conversation begins from where your plan left off — not from a blank page.",
      },
      {
        id: 'edit_plan',
        question: 'Can I change my answers before speaking to a planner?',
        answer: "Yes. Use 'Edit My Plan' to return to any step and update your answers. Your updated plan will be reflected when your planner reviews it.",
      },
    ],
  },
};

// ── Persistence helper ────────────────────────────────────────────────────────
const STORAGE_KEY = 'prashasta_event_journey';

export function trackHelpTopicViewed(topicId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const state = raw ? JSON.parse(raw) : {};
    const viewed = Array.isArray(state.help_topics_viewed) ? state.help_topics_viewed : [];
    const key = topicId.toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    if (!viewed.includes(key)) {
      state.help_topics_viewed = [...viewed, key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch { /* silently ignore */ }
}

// ── Simple fuzzy match for free-text input ────────────────────────────────────
/**
 * Generic English stop-words that carry no domain signal.
 * Removing these ensures a query like "what is the weather today"
 * cannot trigger a false-positive match through incidental word overlap.
 */
const _STOPWORDS = new Set([
  // interrogatives & determiners
  'what', 'when', 'where', 'which', 'while', 'whose', 'whom',
  'this', 'that', 'these', 'those',
  // prepositions & conjunctions
  'with', 'from', 'into', 'onto', 'upon', 'about', 'after', 'along',
  'among', 'around', 'before', 'below', 'above', 'beyond', 'between',
  'across', 'against', 'beside', 'through', 'during',
  // temporal / frequency (high false-positive risk)
  'today', 'tonight', 'tomorrow', 'yesterday', 'always', 'never', 'often',
  // pronouns / references
  'there', 'their', 'again', 'every', 'still', 'right', 'only', 'even',
  'just', 'also', 'very', 'really', 'other', 'same', 'such', 'each',
  'much', 'many', 'both', 'most', 'some', 'more', 'your', 'their',
  // modals & auxiliaries
  'could', 'would', 'should', 'might', 'shall',
  // present-tense filler verbs (domain-neutral)
  'being', 'having', 'going', 'doing', 'making', 'taking', 'giving',
  'coming', 'using', 'trying', 'getting', 'looking', 'asking', 'telling',
  'showing', 'feels', 'seems', 'looks', 'means', 'needs', 'works',
  'helps', 'think', 'know', 'like', 'want', 'need',
]);

/**
 * findBestMatch — weighted, stop-word-filtered keyword matcher.
 *
 * Scoring rules (in order of precedence):
 *  1. Exact phrase (≥ 4 chars) found in Q+A → immediate strong return.
 *  2. Meaningful keyword extraction:
 *     - Tokens are stripped of punctuation, lowercased.
 *     - Short tokens (< 4 chars) and stop-words are discarded.
 *  3. Threshold to qualify a match:
 *     - Any keyword ≥ 5 chars ("domain token") present → threshold = 1 hit.
 *     - All keywords are exactly 4 chars → threshold = 2 hits (stricter).
 *     This lets single domain queries like "venue" or "budget" match correctly
 *     while preventing "what is the weather today" (no domain tokens in the
 *     event-planning vocabulary) from scoring ≥ 1.
 */
export function findBestMatch(freeText, stepTopics) {
  if (!freeText?.trim() || !stepTopics?.questions?.length) return null;
  const normalised = freeText.toLowerCase().trim();

  // Phase 1: exact phrase match (≥ 4 chars → immediate strong signal)
  if (normalised.length >= 4) {
    for (const q of stepTopics.questions) {
      const combined = (q.question + ' ' + q.answer).toLowerCase();
      if (combined.includes(normalised)) return q;
    }
  }

  // Phase 2: keyword extraction — strip punctuation, filter stop-words + short tokens
  const tokens = normalised.split(/[\s\W]+/).filter(Boolean);
  const keywords = tokens.filter(w => w.length >= 4 && !_STOPWORDS.has(w));
  if (keywords.length === 0) return null;

  // Determine threshold: stricter (2 hits) when all keywords are 4-char borderline tokens
  const hasDomainToken = keywords.some(w => w.length >= 5);
  const threshold = hasDomainToken ? 1 : 2;

  let best = null;
  let bestScore = 0;

  for (const q of stepTopics.questions) {
    const combined = (q.question + ' ' + q.answer).toLowerCase();
    const score = keywords.reduce((acc, w) => acc + (combined.includes(w) ? 1 : 0), 0);
    if (score > bestScore) {
      bestScore = score;
      best = q;
    }
  }

  return bestScore >= threshold ? best : null;
}
